import { useEffect, useRef, useState } from "react";

/**
 * Makes the whole window a drop target for files. Use it on a view with exactly
 * one place a file can go (e.g. org settings → logo), and pair it with
 * <FileDropOverlay> plus a visible upload button.
 *
 * - Only drags carrying files react; dragged text and links are left alone.
 * - With `enabled` false (the viewer can't upload) no overlay shows, and a
 *   dropped file is swallowed instead of the browser opening it.
 * - A drop an inner drop target already handled (`preventDefault`) is skipped.
 *
 * Returns whether files are being dragged over the window.
 */
export function usePageFileDrop({ enabled, onFiles }: { enabled: boolean; onFiles: (files: File[]) => void }): boolean {
  const [dragging, setDragging] = useState(false);
  const onFilesRef = useRef(onFiles);
  useEffect(() => {
    onFilesRef.current = onFiles;
  });

  useEffect(() => {
    // dragenter/dragleave fire for every element crossed; count them so moving
    // over children doesn't flicker the overlay.
    let depth = 0;
    const isFileDrag = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes("Files");

    const onEnter = (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      depth += 1;
      if (enabled) setDragging(true);
    };
    const onLeave = (e: DragEvent) => {
      if (!isFileDrag(e)) return;
      depth = Math.max(0, depth - 1);
      if (depth === 0) setDragging(false);
    };
    const onOver = (e: DragEvent) => {
      if (!isFileDrag(e) || e.defaultPrevented) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = enabled ? "copy" : "none";
    };
    const onDrop = (e: DragEvent) => {
      depth = 0;
      setDragging(false);
      if (!isFileDrag(e) || e.defaultPrevented) return;
      e.preventDefault();
      const files = Array.from(e.dataTransfer?.files ?? []);
      if (enabled && files.length > 0) onFilesRef.current(files);
    };

    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("dragover", onOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("drop", onDrop);
      setDragging(false);
    };
  }, [enabled]);

  return dragging;
}
